'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ZonaFisica {
  id: string;
  nome: string;
  tipo: string;
}

interface SensorePorta {
  id: string;
  nome: string;
  zona_id: string;
  stato: string;
  porta_aperta: boolean;
  ultimo_evento: string;
  modello: string;
  note: string;
  zona?: ZonaFisica;
}

interface AccessoFisico {
  id: string;
  persona: string;
  badge_id: string;
  tipo: string;
  zona_id: string;
  timestamp: string;
  autorizzato: boolean;
  motivo: string;
  zona?: ZonaFisica;
}

interface SensoreAmbientale {
  id: string;
  nome: string;
  tipo: string;
  stato: string;
  zona_id: string;
  posizione: string;
  soglia_temp_min: number;
  soglia_temp_max: number;
  soglia_umidita_min: number;
  soglia_umidita_max: number;
  ultima_lettura: string;
  zona?: ZonaFisica;
}

const STATO_BG: Record<string, string> = {
  online: 'bg-emerald-500/20 text-emerald-400',
  offline: 'bg-gray-500/20 text-gray-400',
  allarme: 'bg-red-500/20 text-red-400',
  warning: 'bg-amber-500/20 text-amber-400',
  manutenzione: 'bg-blue-500/20 text-blue-400',
};

const TIPO_ACCESSO_BG: Record<string, string> = {
  entrata: 'bg-emerald-500/20 text-emerald-400',
  uscita: 'bg-sky-500/20 text-sky-400',
  tentativo_fallito: 'bg-red-500/20 text-red-400',
  allarme: 'bg-red-700/30 text-red-300',
};

export default function SensoriSalaServerContent() {
  const [sensoriPorta, setSensoriPorta] = useState<SensorePorta[]>([]);
  const [accessi, setAccessi] = useState<AccessoFisico[]>([]);
  const [sensoriAmbientali, setSensoriAmbientali] = useState<SensoreAmbientale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'sensori' | 'porte' | 'accessi'>('sensori');
  const [showAddAccesso, setShowAddAccesso] = useState(false);
  const [showAddSensorePorta, setShowAddSensorePorta] = useState(false);
  const [editSensorePorta, setEditSensorePorta] = useState<SensorePorta | null>(null);
  const [zone, setZone] = useState<ZonaFisica[]>([]);
  const [formAccesso, setFormAccesso] = useState({ persona: '', badge_id: '', tipo: 'entrata', zona_id: '', motivo: '' });
  const [formSensorePorta, setFormSensorePorta] = useState({ nome: '', zona_id: '', modello: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [ultimeLettureMap, setUltimeLettureMap] = useState<Record<string, { temperatura: number | null; umidita: number | null }>>({});

  const fetchData = useCallback(async () => {
    try {
      const [portaRes, accessiRes, sensoriRes, zoneRes] = await Promise.all([
        supabase.from('sensori_porta').select('*, zona:zone_fisiche(id,nome,tipo)').order('nome'),
        supabase.from('accessi_fisici').select('*, zona:zone_fisiche(id,nome,tipo)').order('timestamp', { ascending: false }).limit(50),
        supabase.from('sensori_ambientali').select('*, zona:zone_fisiche(id,nome,tipo)').in('tipo', ['temperatura', 'umidita', 'temperatura_umidita']).order('nome'),
        supabase.from('zone_fisiche').select('id,nome,tipo').order('nome'),
      ]);
      if (portaRes.error) throw portaRes.error;
      if (accessiRes.error) throw accessiRes.error;
      if (sensoriRes.error) throw sensoriRes.error;
      if (zoneRes.error) throw zoneRes.error;

      setSensoriPorta(portaRes.data || []);
      setAccessi(accessiRes.data || []);
      setSensoriAmbientali(sensoriRes.data || []);
      setZone(zoneRes.data || []);

      const ids = (sensoriRes.data || []).map((s: SensoreAmbientale) => s.id);
      const map: Record<string, { temperatura: number | null; umidita: number | null }> = {};
      for (const sid of ids) {
        const { data: lettura } = await supabase
          .from('letture_sensori')
          .select('temperatura, umidita')
          .eq('sensore_id', sid)
          .order('timestamp', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (lettura) map[sid] = { temperatura: lettura.temperatura, umidita: lettura.umidita };
      }
      setUltimeLettureMap(map);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddAccesso = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error: err } = await supabase.from('accessi_fisici').insert({
        persona: formAccesso.persona,
        badge_id: formAccesso.badge_id,
        tipo: formAccesso.tipo,
        zona_id: formAccesso.zona_id || null,
        motivo: formAccesso.motivo,
        autorizzato: formAccesso.tipo !== 'tentativo_fallito',
      });
      if (err) throw err;
      setShowAddAccesso(false);
      setFormAccesso({ persona: '', badge_id: '', tipo: 'entrata', zona_id: '', motivo: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAddSensorePorta = () => {
    setEditSensorePorta(null);
    setFormSensorePorta({ nome: '', zona_id: '', modello: '', note: '' });
    setShowAddSensorePorta(true);
  };

  const openEditSensorePorta = (s: SensorePorta) => {
    setEditSensorePorta(s);
    setFormSensorePorta({ nome: s.nome, zona_id: s.zona_id || '', modello: s.modello || '', note: s.note || '' });
    setShowAddSensorePorta(true);
  };

  const handleSaveSensorePorta = async () => {
    if (!formSensorePorta.nome.trim()) { setError('Nome sensore obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { nome: formSensorePorta.nome, zona_id: formSensorePorta.zona_id || null, modello: formSensorePorta.modello, note: formSensorePorta.note, stato: 'online', porta_aperta: false };
      if (editSensorePorta) {
        const { error: err } = await supabase.from('sensori_porta').update({ nome: formSensorePorta.nome, zona_id: formSensorePorta.zona_id || null, modello: formSensorePorta.modello, note: formSensorePorta.note }).eq('id', editSensorePorta.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('sensori_porta').insert(payload);
        if (err) throw err;
      }
      setShowAddSensorePorta(false);
      setEditSensorePorta(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSensorePorta = async (id: string) => {
    if (!confirm('Eliminare questo sensore porta?')) return;
    try {
      const { error: err } = await supabase.from('sensori_porta').delete().eq('id', id);
      if (err) throw err;
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getTempColor = (temp: number | null | undefined, min: number, max: number) => {
    if (temp === null || temp === undefined) return 'text-gray-400';
    if (temp > max) return 'text-red-400';
    if (temp < min) return 'text-blue-400';
    return 'text-emerald-400';
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Sensori Sala Server</h1>
            <p className="text-muted-color text-sm mt-1">Monitoraggio sensori ambientali, porte e accessi fisici</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            {activeTab === 'accessi' && (
              <button onClick={() => setShowAddAccesso(true)} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
                <Icon name="PlusIcon" size={16} />
                Registra Accesso
              </button>
            )}
            {activeTab === 'porte' && (
              <button onClick={openAddSensorePorta} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
                <Icon name="PlusIcon" size={16} />
                Aggiungi Sensore Porta
              </button>
            )}
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>}

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Sensori Ambientali</p>
            <p className="text-2xl font-bold text-white">{sensoriAmbientali.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Sensori Porta</p>
            <p className="text-2xl font-bold text-white">{sensoriPorta.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Porte Aperte</p>
            <p className={`text-2xl font-bold ${sensoriPorta.filter(s => s.porta_aperta).length > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {sensoriPorta.filter(s => s.porta_aperta).length}
            </p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Accessi Oggi</p>
            <p className="text-2xl font-bold text-white">
              {accessi.filter(a => new Date(a.timestamp).toDateString() === new Date().toDateString()).length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface rounded-lg p-1 w-fit border border-border">
          {(['sensori', 'porte', 'accessi'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors capitalize ${activeTab === tab ? 'bg-sky-500 text-white' : 'text-muted-color hover:text-white'}`}>
              {tab === 'sensori' ? 'Sensori Ambientali' : tab === 'porte' ? 'Sensori Porta' : 'Registro Accessi'}
            </button>
          ))}
        </div>

        {/* Sensori Ambientali */}
        {activeTab === 'sensori' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sensoriAmbientali.map(s => {
              const lettura = ultimeLettureMap[s.id];
              const temp = lettura?.temperatura ?? null;
              const umid = lettura?.umidita ?? null;
              return (
                <div key={s.id} className="bg-surface rounded-xl border border-border p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white font-semibold text-sm">{s.nome}</p>
                      <p className="text-muted-color text-xs">{s.posizione}</p>
                      <p className="text-muted-color text-xs">{(s as any).zona?.nome || '—'}</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATO_BG[s.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                      {s.stato}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {temp !== null && (
                      <div className="bg-surface-elevated rounded-lg p-3 text-center">
                        <p className="text-muted-color text-xs mb-1">Temperatura</p>
                        <p className={`text-xl font-bold ${getTempColor(temp, s.soglia_temp_min, s.soglia_temp_max)}`}>{temp.toFixed(1)}°C</p>
                        <p className="text-muted-color text-xs mt-1">Soglia: {s.soglia_temp_min}–{s.soglia_temp_max}°C</p>
                      </div>
                    )}
                    {umid !== null && (
                      <div className="bg-surface-elevated rounded-lg p-3 text-center">
                        <p className="text-muted-color text-xs mb-1">Umidità</p>
                        <p className="text-xl font-bold text-sky-400">{umid.toFixed(1)}%</p>
                        <p className="text-muted-color text-xs mt-1">Soglia: {s.soglia_umidita_min}–{s.soglia_umidita_max}%</p>
                      </div>
                    )}
                  </div>
                  <p className="text-muted-color text-xs mt-3">
                    Ultima lettura: {s.ultima_lettura ? new Date(s.ultima_lettura).toLocaleString('it-IT') : '—'}
                  </p>
                </div>
              );
            })}
            {sensoriAmbientali.length === 0 && (
              <div className="col-span-3 text-center py-12 text-muted-color">Nessun sensore ambientale trovato</div>
            )}
          </div>
        )}

        {/* Sensori Porta */}
        {activeTab === 'porte' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sensoriPorta.map(s => (
              <div key={s.id} className={`bg-surface rounded-xl border p-5 ${s.porta_aperta ? 'border-red-500/50' : s.stato === 'allarme' ? 'border-amber-500/40' : 'border-border'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{s.nome}</p>
                    <p className="text-muted-color text-xs">{s.zona?.nome || '—'}</p>
                    {s.modello && <p className="text-muted-color text-xs">{s.modello}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${STATO_BG[s.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                      {s.stato}
                    </span>
                    <button onClick={() => openEditSensorePorta(s)} className="p-1 rounded hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                      <Icon name="PencilIcon" size={12} />
                    </button>
                    <button onClick={() => handleDeleteSensorePorta(s.id)} className="p-1 rounded hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                      <Icon name="TrashIcon" size={12} />
                    </button>
                  </div>
                </div>
                <div className={`rounded-lg p-3 text-center ${s.porta_aperta ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
                  <p className={`text-lg font-bold ${s.porta_aperta ? 'text-red-400' : 'text-emerald-400'}`}>
                    {s.porta_aperta ? '🔓 APERTA' : '🔒 CHIUSA'}
                  </p>
                </div>
                {s.ultimo_evento && (
                  <p className="text-xs text-muted-color mt-2">Ultimo evento: {new Date(s.ultimo_evento).toLocaleString('it-IT')}</p>
                )}
              </div>
            ))}
            {sensoriPorta.length === 0 && (
              <div className="col-span-3 text-center py-12 text-muted-color">
                <Icon name="LockClosedIcon" size={48} className="mx-auto mb-3 opacity-30" />
                <p>Nessun sensore porta configurato</p>
                <button onClick={openAddSensorePorta} className="mt-3 text-sky-400 hover:text-sky-300 text-sm font-semibold">+ Aggiungi sensore porta</button>
              </div>
            )}
          </div>
        )}

        {/* Registro Accessi */}
        {activeTab === 'accessi' && (
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">DATA/ORA</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">PERSONA</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">BADGE</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">TIPO</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">ZONA</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">AUTORIZZATO</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">MOTIVO</th>
                </tr>
              </thead>
              <tbody>
                {accessi.map(a => (
                  <tr key={a.id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                    <td className="px-4 py-3 text-secondary-color text-sm">{new Date(a.timestamp).toLocaleString('it-IT')}</td>
                    <td className="px-4 py-3 text-white text-sm font-semibold">{a.persona}</td>
                    <td className="px-4 py-3 text-muted-color text-xs font-mono">{a.badge_id || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIPO_ACCESSO_BG[a.tipo] || 'bg-gray-500/20 text-gray-400'}`}>
                        {a.tipo.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-secondary-color text-sm">{(a as any).zona?.nome || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.autorizzato ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {a.autorizzato ? 'Sì' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-color text-xs">{a.motivo || '—'}</td>
                  </tr>
                ))}
                {accessi.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-color text-sm">Nessun accesso registrato</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Aggiungi Accesso */}
        {showAddAccesso && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Registra Accesso</h3>
                <button onClick={() => setShowAddAccesso(false)} className="text-muted-color hover:text-white">
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>
              <form onSubmit={handleAddAccesso} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Persona *</label>
                  <input value={formAccesso.persona} onChange={e => setFormAccesso(p => ({ ...p, persona: e.target.value }))} required
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Badge ID</label>
                  <input value={formAccesso.badge_id} onChange={e => setFormAccesso(p => ({ ...p, badge_id: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Tipo</label>
                  <select value={formAccesso.tipo} onChange={e => setFormAccesso(p => ({ ...p, tipo: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                    <option value="entrata">Entrata</option>
                    <option value="uscita">Uscita</option>
                    <option value="tentativo_fallito">Tentativo Fallito</option>
                    <option value="allarme">Allarme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                  <select value={formAccesso.zona_id} onChange={e => setFormAccesso(p => ({ ...p, zona_id: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                    <option value="">— Seleziona zona —</option>
                    {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Motivo</label>
                  <input value={formAccesso.motivo} onChange={e => setFormAccesso(p => ({ ...p, motivo: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowAddAccesso(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : 'Registra'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add/Edit Sensore Porta Modal */}
        {showAddSensorePorta && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white">{editSensorePorta ? 'Modifica Sensore Porta' : 'Aggiungi Sensore Porta'}</h3>
                <button onClick={() => { setShowAddSensorePorta(false); setEditSensorePorta(null); }} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                  <input value={formSensorePorta.nome} onChange={e => setFormSensorePorta(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={formSensorePorta.zona_id} onChange={e => setFormSensorePorta(p => ({ ...p, zona_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Modello</label>
                    <input value={formSensorePorta.modello} onChange={e => setFormSensorePorta(p => ({ ...p, modello: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Note</label>
                  <textarea value={formSensorePorta.note} onChange={e => setFormSensorePorta(p => ({ ...p, note: e.target.value }))} rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500 resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => { setShowAddSensorePorta(false); setEditSensorePorta(null); }} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={handleSaveSensorePorta} disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                  {saving ? 'Salvataggio...' : editSensorePorta ? 'Aggiorna' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
